import React, { useEffect, useState } from 'react';
import LoadingSpinner from "../../LoadingSpinner";
import useModalStore from "../../stores/modalStore";
import { LOCATION_ID_TO_NAME } from "../../utils/constants";
import {fetchUserEventList, handleDelete} from "../../services/eventService";
import useEventStore from "../../stores/eventStore";
import useUserStore from "../../stores/userStore";
import { hasPermission } from "../../utils/permissions";
import { Trash2, Pencil, Loader2, Settings } from "lucide-react";
import EventDetailModal from "../modals/EventDetailModal";
import useSettingsStore from "../../stores/settingsStore";

const eventTypeBadge = (type) => {
    const base = "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap";
    switch (type) {
        case "clock in": return `${base} bg-emerald-100 text-emerald-700`;
        case "clock out": return `${base} bg-rose-100 text-rose-700`;
        case "sick": return `${base} bg-purple-100 text-purple-700`;
        case "time off": return `${base} bg-blue-100 text-blue-700`;
        case "note": return `${base} bg-amber-100 text-amber-700`;
        case "comped service": return `${base} bg-teal-100 text-teal-700`;
        default: return `${base} bg-gray-100 text-gray-600`;
    }
};

const EventsTable = ({ userId }) => {
    const [deletingId, setDeletingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [detailEvent, setDetailEvent] = useState(null);
    const openModal = useModalStore(state => state.openModal);
    const { currentUser, setRequestList, userIdToFirstName, employeeEvents, setEmployeeEvents } = useUserStore();
    const canEditOthers = hasPermission('edit_events_for_others');
    const { setSelectedEvent } = useEventStore();
    const piiClass = useSettingsStore(s => s.blurPII) ? 'blur-pii' : '';
    const isLoading = employeeEvents[userId]?.length === 0;
    const events = employeeEvents[userId];
    const [showEventId, setShowEventId] = useState(false);

    const [timestampModes, setTimestampModes] = useState({
        event: 'locale', // 'locale' or 'epoch'
    });

    const toggleTimestampMode = (key) => {
        setTimestampModes(prev => ({
            ...prev,
            [key]: prev[key] === 'locale' ? 'epoch' : 'locale'
        }));
    };

    useEffect(() => {
        if (!userId) return;
        if (!currentUser.authenticated) return;
        fetchUserEventList(userId, setEmployeeEvents, "eventsTable load");
    }, [userId, setEmployeeEvents, currentUser.authenticated]);

    const toggleSelect = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const deleteSelected = async () => {
        for (const id of selectedIds) {
            const event = employeeEvents[userId]?.find((e) => e.id === id);
            if (!event) continue;

            await handleDelete(event, currentUser.user_id, setDeletingId, async () => {
                setEmployeeEvents(userId, (prev) => prev.filter((e) => e.id !== event.id));
                setRequestList((prev) => prev.filter((e) => e.id !== event.id));
            });
        }
        setSelectedIds([]);
    };

    useEffect(() => {
        console.log("Event list of current user was updated. Re-rendering events table");
    }, [events]);

    return (
        <div className="p-4 sm:p-6">
            {canEditOthers && selectedIds.length > 0 && (
                <div className="mb-3 flex items-center gap-3">
                    <button
                        className="!inline-flex !items-center !gap-1.5 !px-3 !py-1.5 !text-xs !font-medium !bg-red-600 !text-white !border-none !rounded-lg hover:!bg-red-700 !transition-colors"
                        onClick={deleteSelected}
                    >
                        <Trash2 size={13} /> Delete Selected ({selectedIds.length})
                    </button>
                    <button
                        className="!text-xs !text-gray-500 !bg-transparent !border !border-gray-200 !rounded-lg !px-3 !py-1.5 hover:!bg-gray-50 !transition-colors"
                        onClick={() => setSelectedIds([])}
                    >
                        Clear
                    </button>
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
                    <Loader2 size={20} className="animate-spin" /> Loading events...
                </div>
            ) : (
                <div className="overflow-x-auto -mx-4 sm:-mx-6">
                    <table className="w-full text-sm">
                        <thead>
                        <tr className="border-b border-gray-100">
                            {canEditOthers && <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide w-8"></th>}
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">User</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Event</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Location</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => toggleTimestampMode('event')}>
                                Time
                            </th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide max-w-[180px]">Notes</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Value</th>
                            {canEditOthers && (
                                <>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide cursor-pointer" onClick={() => setShowEventId((prev) => !prev)}>
                                        ID {showEventId ? "▾" : "▸"}
                                    </th>
                                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Seq</th>
                                    <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide w-20">Actions</th>
                                </>
                            )}
                        </tr>
                        </thead>
                        <tbody>
                        {Array.isArray(events) &&
                            events.map((event) => (
                                <tr key={event.id} className="border-b border-gray-50 hover:bg-indigo-50/30 transition-colors">
                                    {canEditOthers && (
                                        <td className="px-4 py-2">
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(event.id)}
                                                onChange={() => toggleSelect(event.id)}
                                                className="rounded"
                                            />
                                        </td>
                                    )}

                                    <td className={`px-4 py-2 text-gray-700 font-medium text-left ${piiClass}`}>
                                        {userIdToFirstName[userId] || userId}
                                    </td>

                                    <td className="px-4 py-2 text-left">
                                        <span className={eventTypeBadge(event.event_type)}>{event.event_type}</span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-500 text-xs hidden sm:table-cell text-left">
                                        {LOCATION_ID_TO_NAME[event.location_id]}
                                    </td>

                                    <td className="px-4 py-2 text-gray-600 text-xs whitespace-nowrap text-left">
                                        {event.event_timestamp
                                            ? timestampModes.event === 'epoch'
                                                ? new Date(event.event_timestamp).getTime()
                                                : new Date(event.event_timestamp).toLocaleString()
                                            : "N/A"}
                                    </td>

                                    <td
                                        className="px-4 py-2 text-gray-500 text-xs max-w-[180px] truncate text-left"
                                        title={event.notes || 'N/A'}
                                    >
                                        {event.notes || '—'}
                                    </td>

                                    <td className="px-4 py-2 text-gray-600 text-xs text-left">{event.value || "—"}</td>

                                    {canEditOthers && (
                                        <>
                                            <td className="px-4 py-2 font-mono text-[10px] text-gray-400 text-left">
                                                {showEventId ? event.id : '...'}
                                            </td>
                                            <td className="px-4 py-2 text-gray-500 text-xs text-left">{event.sequence_number || "—"}</td>
                                            <td className="px-4 py-2">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => setDetailEvent(event)}
                                                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 hover:!border-gray-300 hover:!text-gray-600 !text-gray-400 !transition-colors"
                                                        title="View details"
                                                    >
                                                        <Settings size={13} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedEvent(event);
                                                            openModal("editEventModal");
                                                        }}
                                                        className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-indigo-50 hover:!border-indigo-200 hover:!text-indigo-600 !text-gray-400 !transition-colors"
                                                        title="Edit"
                                                    >
                                                        <Pencil size={13} />
                                                    </button>
                                                    {deletingId === event.id ? (
                                                        <div className="p-1.5"><LoadingSpinner small /></div>
                                                    ) : (
                                                        <button
                                                            disabled={deletingId === event.id}
                                                            onClick={() =>
                                                                handleDelete(
                                                                    event,
                                                                    currentUser.user_id,
                                                                    setDeletingId,
                                                                    async () => {
                                                                        setEmployeeEvents(userId, (prev) => prev.filter((e) => e.id !== event.id));
                                                                        setRequestList((prev) => prev.filter((e) => e.id !== event.id));
                                                                    }
                                                                )
                                                            }
                                                            className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-red-50 hover:!border-red-200 hover:!text-red-600 !text-gray-400 !transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 size={13} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {detailEvent && (
                <EventDetailModal
                    event={detailEvent}
                    userIdToFirstName={userIdToFirstName}
                    onClose={() => setDetailEvent(null)}
                />
            )}
        </div>
    );
};

export default EventsTable;
